window.roomBundleBuilderTotals = {
  update(root) {
    if (!root) return;

    const productInputs = Array.from(root.querySelectorAll('[data-room-product]'));
    const selected = productInputs.filter((input) => input.checked && !input.disabled);
    const total = selected.reduce((sum, input) => sum + (Number(input.dataset.price) || 0), 0);
    const totalElement = root.querySelector('[data-room-total]');
    const addButton = root.querySelector('[data-room-add]');
    const locale = root.dataset.locale || document.documentElement.lang || 'en';
    const currency = root.dataset.currency || 'USD';
    let formattedTotal;

    try {
      formattedTotal = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
      }).format(total / 100);
    } catch (error) {
      formattedTotal = `${currency} ${(total / 100).toFixed(2)}`;
    }

    if (totalElement) totalElement.textContent = formattedTotal;
    if (addButton && root.dataset.adding !== 'true') {
      addButton.disabled = selected.length === 0;
      addButton.setAttribute('aria-disabled', String(selected.length === 0));
    }
  },
};

if (Number(window.roomBundleBuilderTotalEventsVersion || 0) < 2) {
  window.roomBundleBuilderTotalEventsVersion = 2;

  const getRoomBundleRoot = (event) => {
    const target = event.target;
    if (!target || typeof target.closest !== 'function') return null;

    const trigger = target.closest('[data-room-product], label[for^="RoomBundleProduct-"]');
    return trigger?.closest('room-bundle-builder') || null;
  };

  const clearRoomBundleMessage = (root) => {
    const messageElement = root?.querySelector('[data-room-message]');
    if (!messageElement) return;

    messageElement.textContent = '';
    messageElement.classList.remove('is-error');
  };

  const handleProductSelection = (event) => {
    const target = event.target;
    const input = target && typeof target.closest === 'function'
      ? target.closest('[data-room-product]')
      : null;
    if (!input) return;

    const root = input.closest('room-bundle-builder');
    if (!root) return;

    clearRoomBundleMessage(root);
    window.roomBundleBuilderTotals.update(root);
  };

  const handleProductClick = (event) => {
    const root = getRoomBundleRoot(event);
    if (!root) return;

    // Run after the checkbox's native click/label activation has updated `checked`.
    window.setTimeout(() => {
      if (!root.isConnected) return;
      clearRoomBundleMessage(root);
      window.roomBundleBuilderTotals.update(root);
    }, 0);
  };

  // Capture-phase listeners still run when theme-editor or app scripts stop bubbling.
  document.addEventListener('input', handleProductSelection, true);
  document.addEventListener('change', handleProductSelection, true);
  document.addEventListener('click', handleProductClick, true);
  document.addEventListener('shopify:section:load', (event) => {
    const roots = event.target.matches?.('room-bundle-builder')
      ? [event.target]
      : Array.from(event.target.querySelectorAll?.('room-bundle-builder') || []);
    roots.forEach((root) => window.roomBundleBuilderTotals.update(root));
  });
}

window.roomBundleBuilderVariants = {
  activeModal: null,
  activeRow: null,
  activeToggle: null,
  previousBodyOverflow: '',

  open(button) {
    const row = button?.closest('[data-room-product-row]');
    const root = button?.closest('room-bundle-builder');
    const template = row?.querySelector('[data-room-variant-template]');
    const modal = root?.querySelector('[data-room-quickview]');
    const content = modal?.querySelector('[data-room-quickview-content]');
    if (!row || !root || !template || !modal || !content) return;

    if (this.activeModal && this.activeModal !== modal) this.close(false);

    this.activeModal = modal;
    this.activeRow = row;
    this.activeToggle = button;
    modal.dataset.activeProductRowId = row.id;
    content.innerHTML = '';
    content.appendChild(template.content.cloneNode(true));

    const product = content.querySelector('[data-room-quickview-product]');
    const input = row.querySelector('[data-room-product]');
    this.applyVariantToControls(product, input?.value);
    this.updateProduct(product);

    modal.hidden = false;
    modal.classList.add('is-open');
    button.setAttribute('aria-expanded', 'true');
    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    modal.querySelector('.room-bundle-builder__quickview-close')?.focus();
  },

  close(restoreFocus = true) {
    const modal = this.activeModal;
    const toggle = this.activeToggle;
    if (!modal) return;

    modal.hidden = true;
    modal.classList.remove('is-open');
    modal.querySelector('[data-room-quickview-content]')?.replaceChildren();
    delete modal.dataset.activeProductRowId;
    toggle?.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = this.previousBodyOverflow;

    this.activeModal = null;
    this.activeRow = null;
    this.activeToggle = null;

    if (restoreFocus && toggle && document.contains(toggle)) toggle.focus();
  },

  getVariants(product) {
    if (!product) return [];
    if (product.roomBundleVariants) return product.roomBundleVariants;

    const data = product.querySelector('[data-room-quickview-variants]');
    try {
      product.roomBundleVariants = JSON.parse(data?.textContent || '[]');
    } catch (error) {
      product.roomBundleVariants = [];
    }
    return product.roomBundleVariants;
  },

  getSelectedOptions(product) {
    const selectedOptions = [];

    product?.querySelectorAll('[data-room-quickview-option-select]').forEach((select) => {
      const position = Number(select.dataset.optionPosition || 1) - 1;
      selectedOptions[position] = select.value;
    });

    product?.querySelectorAll('[data-room-quickview-option-swatch].is-selected').forEach((swatch) => {
      const position = Number(swatch.dataset.optionPosition || 1) - 1;
      selectedOptions[position] = swatch.dataset.optionValue;
    });

    return selectedOptions;
  },

  variantMatchesOptions(variant, selectedOptions) {
    return selectedOptions.every((value, index) => {
      if (!value) return true;
      return String(variant[`option${index + 1}`] || '') === String(value);
    });
  },

  findVariant(product) {
    const variants = this.getVariants(product);
    const selectedOptions = this.getSelectedOptions(product);
    const availableVariant = variants.find(
      (variant) => this.variantMatchesOptions(variant, selectedOptions) && variant.available
    );

    return availableVariant || variants.find((variant) => this.variantMatchesOptions(variant, selectedOptions)) || null;
  },

  applyVariantToControls(product, variantId) {
    const variant = this.getVariants(product).find((item) => String(item.id) === String(variantId));
    if (!variant) return;

    product.querySelectorAll('[data-room-quickview-option-select]').forEach((select) => {
      const position = Number(select.dataset.optionPosition || 1);
      const value = variant[`option${position}`];
      if (value != null) select.value = value;
    });

    product.querySelectorAll('[data-room-quickview-option-group]').forEach((group) => {
      const position = Number(group.dataset.optionPosition || 1);
      const value = variant[`option${position}`];
      group.querySelectorAll('[data-room-quickview-option-swatch]').forEach((swatch) => {
        const isSelected = String(swatch.dataset.optionValue) === String(value);
        swatch.classList.toggle('is-selected', isSelected);
        swatch.setAttribute('aria-pressed', String(isSelected));
      });
    });
  },

  selectSwatch(button) {
    const group = button?.closest('[data-room-quickview-option-group]');
    const product = button?.closest('[data-room-quickview-product]');
    if (!group || !product) return;

    group.querySelectorAll('[data-room-quickview-option-swatch]').forEach((swatch) => {
      const isSelected = swatch === button;
      swatch.classList.toggle('is-selected', isSelected);
      swatch.setAttribute('aria-pressed', String(isSelected));
    });
    this.updateProduct(product);
  },

  updateProduct(product) {
    if (!product) return null;

    product.querySelectorAll('[data-room-quickview-option-group]').forEach((group) => {
      const swatches = Array.from(group.querySelectorAll('[data-room-quickview-option-swatch]'));
      if (!swatches.length || swatches.some((swatch) => swatch.classList.contains('is-selected'))) return;
      swatches[0].classList.add('is-selected');
      swatches[0].setAttribute('aria-pressed', 'true');
    });

    const variant = this.findVariant(product);
    const available = Boolean(variant?.available);
    const confirmButton = product.querySelector('[data-room-quickview-confirm]');
    const confirmLabel = confirmButton?.querySelector('[data-room-quickview-confirm-label]');
    const price = product.querySelector('[data-room-quickview-price]');
    const comparePrice = product.querySelector('[data-room-quickview-compare-price]');
    const status = product.querySelector('[data-room-quickview-status]');

    if (confirmButton) {
      confirmButton.dataset.variantId = variant?.id || '';
      confirmButton.dataset.variantPrice = variant?.priceCents || '0';
      confirmButton.dataset.variantPriceFormatted = variant?.price || '';
      confirmButton.disabled = !available;
    }
    if (confirmLabel) confirmLabel.textContent = available ? 'Select this option' : 'Sold out';
    if (price) price.textContent = variant?.price || '';
    if (comparePrice) {
      comparePrice.textContent = variant?.comparePrice || '';
      comparePrice.hidden = !variant?.comparePrice;
    }
    if (status) status.textContent = available ? '' : 'This option is currently sold out.';

    if (variant?.image) {
      const slides = Array.from(product.querySelectorAll('[data-room-quickview-slide]'));
      const imageIndex = slides.findIndex((slide) => slide.dataset.imageSrc === variant.image);
      if (imageIndex >= 0) this.goToSlide(product, imageIndex);
    }

    if (available) this.applyVariantToRow(variant, product);

    return variant;
  },

  applyVariantToRow(variant, product = null) {
    const modal = product?.closest('[data-room-quickview]') || this.activeModal;
    const rowId = modal?.dataset.activeProductRowId;
    const row = (rowId && document.getElementById(rowId)) || this.activeRow;
    const root = row?.closest('room-bundle-builder');
    const input = row?.querySelector('[data-room-product]');
    const priceElement = row?.querySelector('[data-room-product-price]');
    if (!variant?.available || !row || !root || !input) return;

    input.value = String(variant.id || '');
    input.dataset.price = String(variant.priceCents || 0);
    input.disabled = false;
    row.dataset.selectedVariantId = String(variant.id || '');

    if (priceElement) {
      priceElement.textContent = variant.price || '';
      priceElement.dataset.pricePrefix = '';
    }

    const messageElement = root.querySelector('[data-room-message]');
    if (messageElement) {
      messageElement.textContent = '';
      messageElement.classList.remove('is-error');
    }

    if (typeof root.updateRoomTotal === 'function') root.updateRoomTotal();
    window.roomBundleBuilderTotals?.update(root);
  },

  goToSlide(product, index) {
    const slides = Array.from(product?.querySelectorAll('[data-room-quickview-slide]') || []);
    const track = product?.querySelector('[data-room-quickview-track]');
    if (!slides.length || !track) return;

    const currentIndex = (Number(index) + slides.length) % slides.length;
    product.dataset.galleryIndex = String(currentIndex);
    track.style.transform = `translateX(-${currentIndex * 100}%)`;

    slides.forEach((slide, slideIndex) => {
      slide.setAttribute('aria-hidden', String(slideIndex !== currentIndex));
    });
    product.querySelectorAll('[data-room-quickview-dot]').forEach((dot, dotIndex) => {
      const isActive = dotIndex === currentIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', String(isActive));
    });
  },

  changeSlide(button, direction) {
    const product = button?.closest('[data-room-quickview-product]');
    const currentIndex = Number(product?.dataset.galleryIndex || 0);
    this.goToSlide(product, currentIndex + direction);
  },

  confirm(button) {
    if (!button || button.disabled || !this.activeRow) return;

    const product = button.closest('[data-room-quickview-product]');
    const variant = this.findVariant(product);
    if (variant?.available) this.applyVariantToRow(variant, product);
    this.close(true);
  },
};

if (!window.roomBundleBuilderVariantEventsBound) {
  window.roomBundleBuilderVariantEventsBound = true;

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest?.('[data-room-variant-toggle]');
    if (toggle) {
      event.preventDefault();
      window.roomBundleBuilderVariants.open(toggle);
      return;
    }

    const closeButton = event.target.closest?.('[data-room-quickview-close]');
    if (closeButton) {
      event.preventDefault();
      window.roomBundleBuilderVariants.close(true);
      return;
    }

    const swatch = event.target.closest?.('[data-room-quickview-option-swatch]');
    if (swatch) {
      event.preventDefault();
      window.roomBundleBuilderVariants.selectSwatch(swatch);
      return;
    }

    const previous = event.target.closest?.('[data-room-quickview-previous]');
    if (previous) {
      event.preventDefault();
      window.roomBundleBuilderVariants.changeSlide(previous, -1);
      return;
    }

    const next = event.target.closest?.('[data-room-quickview-next]');
    if (next) {
      event.preventDefault();
      window.roomBundleBuilderVariants.changeSlide(next, 1);
      return;
    }

    const dot = event.target.closest?.('[data-room-quickview-dot]');
    if (dot) {
      event.preventDefault();
      const product = dot.closest('[data-room-quickview-product]');
      window.roomBundleBuilderVariants.goToSlide(product, Number(dot.dataset.slideIndex || 0));
      return;
    }

    const confirmButton = event.target.closest?.('[data-room-quickview-confirm]');
    if (confirmButton) {
      event.preventDefault();
      window.roomBundleBuilderVariants.confirm(confirmButton);
    }
  });

  document.addEventListener('change', (event) => {
    const select = event.target.closest?.('[data-room-quickview-option-select]');
    if (!select) return;
    const product = select.closest('[data-room-quickview-product]');
    window.roomBundleBuilderVariants.updateProduct(product);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (window.roomBundleBuilderVariants.activeModal) {
      window.roomBundleBuilderVariants.close(true);
    }
  });
}

if (!customElements.get('room-bundle-builder')) {
  customElements.define(
    'room-bundle-builder',
    class RoomBundleBuilder extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.slides = Array.from(this.querySelectorAll('[data-room-slide]'));
        this.thumbnails = Array.from(this.querySelectorAll('[data-room-thumbnail]'));
        this.previousButton = this.querySelector('[data-room-previous]');
        this.nextButton = this.querySelector('[data-room-next]');
        this.gallery = this.querySelector('[data-room-gallery]');
        this.productInputs = Array.from(this.querySelectorAll('[data-room-product]'));
        this.extraRows = Array.from(this.querySelectorAll('[data-room-product-extra]'));
        this.moreButton = this.querySelector('[data-room-more]');
        this.totalElement = this.querySelector('[data-room-total]');
        this.addButton = this.querySelector('[data-room-add]');
        this.messageElement = this.querySelector('[data-room-message]');
        this.currentIndex = 0;
        this.pointerStartX = null;
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.autoplayEnabled = this.dataset.autoplay === 'true' && !this.reduceMotion;
        this.autoplayDelay = Math.max(Number(this.dataset.autoplayDelay) || 5000, 2000);
        this.locale = this.dataset.locale || document.documentElement.lang || 'en';
        this.currency = this.dataset.currency || 'USD';

        this.onPrevious = () => this.goTo(this.currentIndex - 1, true);
        this.onNext = () => this.goTo(this.currentIndex + 1, true);
        this.onPointerDown = this.handlePointerDown.bind(this);
        this.onPointerUp = this.handlePointerUp.bind(this);
        this.onBlockSelect = this.handleBlockSelect.bind(this);
        this.onVisibilityChange = this.handleVisibilityChange.bind(this);
        this.onAddSelected = this.addSelectedProducts.bind(this);
        this.onProductSelectionChange = (event) => {
          if (!event.target.matches('[data-room-product]')) return;
          this.clearMessage();
          this.updateTotal();
        };

        this.previousButton?.addEventListener('click', this.onPrevious);
        this.nextButton?.addEventListener('click', this.onNext);
        this.gallery?.addEventListener('pointerdown', this.onPointerDown);
        this.gallery?.addEventListener('pointerup', this.onPointerUp);
        this.gallery?.addEventListener('pointercancel', this.onPointerUp);
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        this.addEventListener('input', this.onProductSelectionChange);
        this.addEventListener('change', this.onProductSelectionChange);

        this.thumbnails.forEach((thumbnail, index) => {
          thumbnail.addEventListener('click', () => this.goTo(index, true));
        });

        this.moreButton?.addEventListener('click', () => this.toggleExtraProducts());
        this.addButton?.addEventListener('click', this.onAddSelected);

        this.addEventListener('mouseenter', () => this.stopAutoplay());
        this.addEventListener('mouseleave', () => this.startAutoplay());
        this.addEventListener('focusin', () => this.stopAutoplay());
        this.addEventListener('focusout', (event) => {
          if (!this.contains(event.relatedTarget)) this.startAutoplay();
        });

        this.goTo(0, false);
        this.updateTotal();
        this.startAutoplay();
      }

      disconnectedCallback() {
        this.stopAutoplay();
        this.previousButton?.removeEventListener('click', this.onPrevious);
        this.nextButton?.removeEventListener('click', this.onNext);
        this.gallery?.removeEventListener('pointerdown', this.onPointerDown);
        this.gallery?.removeEventListener('pointerup', this.onPointerUp);
        this.gallery?.removeEventListener('pointercancel', this.onPointerUp);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        this.removeEventListener('input', this.onProductSelectionChange);
        this.removeEventListener('change', this.onProductSelectionChange);
        this.addButton?.removeEventListener('click', this.onAddSelected);
      }

      goTo(index, userInitiated = false) {
        if (!this.slides.length) return;
        this.currentIndex = (index + this.slides.length) % this.slides.length;

        this.slides.forEach((slide, slideIndex) => {
          const isActive = slideIndex === this.currentIndex;
          slide.classList.toggle('is-active', isActive);
          slide.setAttribute('aria-hidden', String(!isActive));
        });

        this.thumbnails.forEach((thumbnail, thumbnailIndex) => {
          const isActive = thumbnailIndex === this.currentIndex;
          thumbnail.classList.toggle('is-active', isActive);
          thumbnail.setAttribute('aria-current', isActive ? 'true' : 'false');
        });

        this.thumbnails[this.currentIndex]?.scrollIntoView({
          behavior: userInitiated && !this.reduceMotion ? 'smooth' : 'auto',
          block: 'nearest',
          inline: 'nearest',
        });

        if (userInitiated) {
          this.stopAutoplay();
          this.startAutoplay();
        }
      }

      handlePointerDown(event) {
        if (event.pointerType === 'mouse' || event.target.closest('button, a')) return;
        this.pointerStartX = event.clientX;
      }

      handlePointerUp(event) {
        if (this.pointerStartX === null) return;
        const distance = event.clientX - this.pointerStartX;
        this.pointerStartX = null;
        if (Math.abs(distance) < 45) return;
        this.goTo(this.currentIndex + (distance < 0 ? 1 : -1), true);
      }

      handleBlockSelect(event) {
        if (!event.detail?.blockId) return;
        const block = this.querySelector(`[data-block-id="${CSS.escape(event.detail.blockId)}"]`);
        const slide = block?.closest('[data-room-slide]');
        if (!slide) return;
        const index = this.slides.indexOf(slide);
        if (index >= 0) this.goTo(index, false);
        this.stopAutoplay();
      }

      handleVisibilityChange() {
        if (document.hidden) {
          this.stopAutoplay();
        } else {
          this.startAutoplay();
        }
      }

      toggleExtraProducts() {
        if (!this.moreButton || !this.extraRows.length) return;
        const willExpand = this.moreButton.getAttribute('aria-expanded') !== 'true';

        this.extraRows.forEach((row) => {
          row.hidden = !willExpand;
        });

        this.moreButton.setAttribute('aria-expanded', String(willExpand));
        const label = willExpand
          ? this.moreButton.dataset.lessLabel
          : `+ ${this.extraRows.length} ${this.moreButton.dataset.moreLabel}`;
        const labelElement = this.moreButton.querySelector('[data-room-more-text]');
        if (labelElement) labelElement.textContent = label;
        this.moreButton.classList.toggle('is-expanded', willExpand);
      }

      updateTotal() {
        window.roomBundleBuilderTotals.update(this);
      }

      formatMoney(cents) {
        try {
          return new Intl.NumberFormat(this.locale, {
            style: 'currency',
            currency: this.currency,
          }).format(cents / 100);
        } catch (error) {
          return `${this.currency} ${(cents / 100).toFixed(2)}`;
        }
      }

      selectedItems() {
        const grouped = new Map();

        this.querySelectorAll('[data-room-product]').forEach((input) => {
          if (!input.checked || input.disabled || !input.value) return;
          const id = String(input.value);
          const existing = grouped.get(id);
          if (existing) {
            existing.quantity += 1;
          } else {
            grouped.set(id, { id: Number(id), quantity: 1 });
          }
        });

        return Array.from(grouped.values());
      }

      getCartElement() {
        return document.querySelector('cart-popup') || document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      }

      getCartSections(cart) {
        if (window.CartPopupLoader && typeof window.CartPopupLoader.getSectionsToRender === 'function') {
          return window.CartPopupLoader.getSectionsToRender();
        }
        if (!cart || typeof cart.getSectionsToRender !== 'function') return [];
        return cart.getSectionsToRender().map((section) => section.id || section.section).filter(Boolean);
      }

      async renderCart(cart, state) {
        if (window.CartPopupLoader && typeof window.CartPopupLoader.renderResponse === 'function') {
          try {
            await window.CartPopupLoader.renderResponse(state, {
              trigger: this.addButton,
              shouldOpen: true,
            });
            return true;
          } catch (error) {
            console.error(error);
          }
        }

        if (!cart || typeof cart.renderContents !== 'function') return false;
        try {
          if (state.sections) {
            cart.renderContents(state, true);
            return true;
          }
        } catch (error) {
          console.error(error);
        }

        if (typeof cart.refresh === 'function') {
          try {
            await cart.refresh();
            return true;
          } catch (error) {
            console.error(error);
          }
        }
        return false;
      }

      async parseResponse(response) {
        const text = await response.text();
        try {
          return JSON.parse(text);
        } catch (error) {
          throw new Error(window.cartStrings?.error || 'Unable to add selected products.');
        }
      }

      async addSelectedProducts() {
        if (!this.addButton || this.dataset.adding === 'true') return;
        const items = this.selectedItems();
        if (!items.length) {
          this.showMessage(this.dataset.emptyMessage || 'Please select at least one product.', true);
          return;
        }

        const cart = this.getCartElement();
        const sectionIds = this.getCartSections(cart);
        const body = { items };
        if (sectionIds.length) {
          body.sections = sectionIds.join(',');
          body.sections_url = window.location.pathname;
          if (cart && typeof cart.setActiveElement === 'function') cart.setActiveElement(this.addButton);
        }

        this.setLoading(true);
        this.clearMessage();

        try {
          const config = typeof fetchConfig === 'function'
            ? fetchConfig('javascript')
            : {
                method: 'POST',
                headers: { Accept: 'application/javascript' },
              };
          config.headers = config.headers || {};
          config.headers['Content-Type'] = 'application/json';
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          config.body = JSON.stringify(body);

          const cartAddUrl = window.routes?.cart_add_url || '/cart/add';
          const response = await fetch(cartAddUrl.endsWith('.js') ? cartAddUrl : `${cartAddUrl}.js`, config);
          const state = await this.parseResponse(response);
          if (!response.ok || state.status) {
            throw new Error(state.description || state.message || window.cartStrings?.error || 'Unable to add selected products.');
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            try {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'room-bundle-builder',
                productVariantIds: items.map((item) => item.id),
                cartData: state,
              });
            } catch (publishError) {
              console.error(publishError);
            }
          }

          const rendered = await this.renderCart(cart, state);
          if (!rendered) window.location = window.routes?.cart_url || '/cart';
        } catch (error) {
          this.showMessage(error.message || window.cartStrings?.error || 'Unable to add selected products.', true);
        } finally {
          this.setLoading(false);
        }
      }

      setLoading(isLoading) {
        this.dataset.adding = String(isLoading);
        this.addButton?.classList.toggle('loading', isLoading);
        if (this.addButton) this.addButton.disabled = isLoading;
        this.addButton?.querySelector('.loading__spinner')?.classList.toggle('hidden', !isLoading);
        if (!isLoading) this.updateTotal();
      }

      showMessage(message, isError = false) {
        if (!this.messageElement) return;
        this.messageElement.textContent = message;
        this.messageElement.classList.toggle('is-error', isError);
      }

      clearMessage() {
        this.showMessage('', false);
      }

      startAutoplay() {
        if (!this.autoplayEnabled || this.slides.length < 2 || document.hidden) return;
        this.stopAutoplay();
        this.autoplayTimer = window.setInterval(() => this.goTo(this.currentIndex + 1), this.autoplayDelay);
      }

      stopAutoplay() {
        if (!this.autoplayTimer) return;
        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }
  );
}

document
  .querySelectorAll('room-bundle-builder')
  .forEach((root) => window.roomBundleBuilderTotals.update(root));
